import React, { useMemo } from 'react'
import styled from 'styled-components'
import { margin, MarginProps } from 'styled-system'

import { HexToRgba } from 'utils/colors'

import Icon from '../Icon'
import Text from '../Text'
import { isLegacy, LeadDetailsDesignVersion } from '../utils/designVersionHelpers'
import Tooltip from '../Tooltip'

export type Option<D = string> = {
  icon: string | string[]
  iconColor?: string
  label?: string
  value: D
  customRender?: React.ReactNode
}

type Props<D = string> = MarginProps & {
  onClickOption: (value: D) => void
  options: Array<Option<D>>
  value?: D | D[]
  designVersion?: LeadDetailsDesignVersion
}

const QuickLinks = <D extends string>({
  onClickOption,
  options: rawOptions,
  designVersion = 'v1',
  value,
  ...marginProps
}: Props<D>) => {
  const options = useMemo(
    () =>
      rawOptions.map(({ value: optionValue, ...i }) => ({
        ...i,
        isActive: !value ? false : Array.isArray(value) ? value.includes(optionValue) : optionValue === value,
        optionValue,
      })),
    [rawOptions, value],
  )

  return (
    <Container {...marginProps}>
      {options.map(({ icon, iconColor = 'blue1', isActive, optionValue, label, customRender }, index) => (
        <Tooltip title={label} key={index}>
          <IconWrapper
            hasBorder={index !== options.length - 1}
            data-testid="quick-link-item"
            isActive={isActive}
            onClick={() => onClickOption(optionValue)}
            isLegacy={isLegacy(designVersion)}
          >
            {customRender ? (
              <>{customRender}</>
            ) : (
              <>
                <Icon color={iconColor} fontSize={16} icon={icon} />
                {label && (
                  <Text
                    color="blue1"
                    {...(isLegacy(designVersion)
                      ? { ellipsis: true, overflow: 'hidden', whiteSpace: 'nowrap', maxWidth: '140px' }
                      : {})}
                  >
                    {label}
                  </Text>
                )}
              </>
            )}
          </IconWrapper>
        </Tooltip>
      ))}
    </Container>
  )
}

const Container = styled.div<MarginProps>`
  ${margin}
  border: 1px solid ${(p) => p.theme.colors.blue1};
  border-radius: 4px;
  display: flex;
  height: max-content;
  width: 100%;
  max-width: fit-content;
`

const IconWrapper = styled.div<{ isActive: boolean; isLegacy: boolean; hasBorder: boolean }>`
  align-items: center;
  background-color: ${(p) => (p.isActive ? HexToRgba(p.theme.colors.blue2, 0.25) : p.theme.colors.transparent)};
  cursor: ${(p) => (p.isActive ? 'default' : 'pointer')};
  user-select: none;
  display: flex;
  justify-content: center;
  gap: ${({ isLegacy }) => (isLegacy ? '0.5rem' : '8px')};
  padding: ${({ isLegacy }) => (isLegacy ? '0.7rem 1.5rem' : '8px 16px')};
  line-height: ${({ isLegacy }) => (isLegacy ? 'inherit' : '20px')};
  max-width: ${({ isLegacy }) => (!isLegacy ? 'inherit' : '140px')};
  border-right: ${({ hasBorder, theme }) => (hasBorder ? `1px solid ${theme.colors.blue2}` : 'none')};
  ${(p) =>
    !p.isActive
      ? `&:hover {
    background-color: ${HexToRgba(p.theme.colors.blue2, 0.1)};
  }`
      : ''}
`

export default QuickLinks
